import { initializeApp, getApps, getApp } from 'firebase/app';
import { getAnalytics } from 'firebase/analytics';
import { initializeAuth, browserLocalPersistence, browserPopupRedirectResolver, getAuth, connectAuthEmulator } from 'firebase/auth';
import { getFirestore, getFirestore as getDB, connectFirestoreEmulator } from 'firebase/firestore';
import { getFunctions, connectFunctionsEmulator } from 'firebase/functions';
import { getStorage } from 'firebase/storage';

// Your web app's Firebase configuration
const firebaseConfig = {
    apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
    authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
    projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
    storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
    appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

// Validate config
const requiredKeys = [
    'apiKey',
    'authDomain',
    'projectId',
    'storageBucket',
    'messagingSenderId',
    'appId'
] as const;

const missingKeys = requiredKeys.filter(key => !firebaseConfig[key]);

if (missingKeys.length > 0) {
    const errorMsg = `Missing required Firebase configuration keys: ${missingKeys.join(', ')}. Check your .env.local file.`;
    throw new Error(errorMsg);
}

// Initialize Firebase
let app: ReturnType<typeof initializeApp>;
let analytics: ReturnType<typeof getAnalytics> | undefined;
let auth: ReturnType<typeof getAuth>;
let db: ReturnType<typeof getFirestore>;
let functions: ReturnType<typeof getFunctions>;
let storage: ReturnType<typeof getStorage>;

try {
    if (getApps().length > 0) {
        app = getApp();
    } else {
        app = initializeApp(firebaseConfig);
    }

    // Analytics is only for browser
    if (typeof window !== 'undefined') {
        analytics = getAnalytics(app);
    }

    // Guard Auth initialization
    try {
        auth = initializeAuth(app, {
            persistence: browserLocalPersistence,
            popupRedirectResolver: browserPopupRedirectResolver,
        });
    } catch (authError: any) {
        if (authError.code === 'auth/already-initialized') {
            auth = getAuth(app);
        } else {
            throw authError;
        }
    }

    db = getFirestore(app);
    functions = getFunctions(app, 'us-central1');
    storage = getStorage(app);

    // Connect to emulator in development mode (only if not explicitly disabled)
    const useEmulator = import.meta.env.DEV && import.meta.env.VITE_USE_EMULATOR !== 'false';
    if (useEmulator) {
        connectFunctionsEmulator(functions, 'localhost', 5001);
        connectFirestoreEmulator(db, 'localhost', 8080);
        connectAuthEmulator(auth, 'http://localhost:9099', { disableWarnings: true });
    }
} catch (e: any) {
    console.error("Firebase initialization failed:", e);
    throw new Error(`Firebase initialization error: ${e.message}`, { cause: e });
}

export { app, analytics, auth, db, functions, storage };
